/**
 * ショットコメントのディープリンクチップ用ホバープレビュー。
 *
 * クリック（プレビューへ遷移してスクロール・再生）とは独立に、ホバーだけで
 *   - コメント本文（投稿者・日時つき）
 *   - ピン付きコメントならピン先メディア
 *     （動画: ピン位置からミュート自動再生 / 音声: メッセンジャーと同じ
 *       コンパクトプレイヤー / 画像: サムネイル）
 * を覗き見できるようにする。
 *
 * - ホバーできない端末（タッチのみ）ではラップせず、従来どおりタップ = 遷移のみ。
 * - データはカードが初めて開かれたときに取得する（RTK Query キャッシュ共有）。
 * - 共有ビュー等で取得できない場合は「読み込めませんでした」を出すだけで、
 *   リンク自体の挙動（クリック遷移）は変えない。
 */
import {
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Tippy from "@tippyjs/react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { selectIsAudioEnabled } from "../../app/actions/audio/audioSlice";
import {
  useGetShotCommentDetailQuery,
  type ShotComment,
} from "../../app/services/shotComments";
import {
  useGetMediaItemsQuery,
  type MediaItem,
} from "../../app/services/mediaItems";
import { extractDetailEntity } from "../../utils/detailResponse";
import { appendToTippyContainer } from "../../utils/tippy";
import {
  DEFAULT_FPS,
  extractFirstTimecode,
  formatTimecode,
  hasTimecode,
  parseTimecode,
} from "../../utils/timecode";
import UserAvatar from "../avatar/UserAvatar";
import MediaPreview from "../media-preview/MediaPreview";
import type { MediaPreviewHandle } from "../../types/media-preview";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../date-display/DateDisplay";
import "../tooltip/tooltip_tippy.css";

// ── スタイル ──────────────────────────────────────────────────────────

const CARD_WIDTH = 288;

const Card = styled.div`
  width: ${CARD_WIDTH}px;
  max-width: calc(100vw - 48px);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: default;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const AuthorName = styled.span.attrs({ className: "bodytext-11" })`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const HeaderDate = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
  flex-shrink: 0;
  margin-left: auto;
`;

const Body = styled.p.attrs({ className: "bodytext-11" })`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
  overflow: hidden;
`;

const StateText = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const MediaArea = styled.div`
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  background: var(--soremo-background);
`;

const PinVideoEl = styled.video`
  display: block;
  width: 100%;
  max-height: 162px;
  object-fit: contain;
  background: #000;
`;

const PinImage = styled.img`
  display: block;
  width: 100%;
  max-height: 162px;
  object-fit: contain;
  background: #000;
`;

/** 動画・フレーム画像の左下に重ねるピン位置バッジ */
const TimecodeBadge = styled.span.attrs({ className: "bodytext-11" })`
  position: absolute;
  left: 6px;
  bottom: 6px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.62);
  color: #fff;

  & > .material-symbols-rounded {
    font-size: 12px;
  }
`;

// ── ピン先メディア ────────────────────────────────────────────────────

interface PinVideoProps {
  item: MediaItem;
  poster: string | null;
  startSeconds: number;
  /** グローバルオーディオコントロールが ON なら音ありで再生を試みる */
  withSound: boolean;
}

/**
 * ピン位置からの自動再生。音の有無はグローバルオーディオコントロールに従い、
 * 音あり再生がブラウザにブロックされた場合はミュートにフォールバックする。
 * MediaPreview（VideoPreview）は開始位置指定とミュート制御を持たないため、
 * 覗き見専用の素の video をここでだけ使う。
 */
const PinVideo = ({ item, poster, startSeconds, withSound }: PinVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // optimized_url が HLS プレイリストのときはネイティブ再生できないため原本を使う
  const src = item.is_hls
    ? item.original_url
    : item.optimized_url || item.original_url;

  const handleLoadedMetadata = () => {
    const el = videoRef.current;
    if (!el) return;
    if (
      startSeconds > 0 &&
      Number.isFinite(el.duration) &&
      startSeconds < el.duration
    ) {
      el.currentTime = startSeconds;
    }
    el.muted = !withSound;
    el.play().catch(() => {
      // 音あり再生がブロックされたらミュートで続行。それでも失敗したら諦める
      el.muted = true;
      el.play().catch(() => undefined);
    });
  };

  return (
    <PinVideoEl
      ref={videoRef}
      src={src}
      poster={poster ?? undefined}
      muted={!withSound}
      playsInline
      preload="metadata"
      onLoadedMetadata={handleLoadedMetadata}
    />
  );
};

interface PinAudioProps {
  item: MediaItem;
  startSeconds: number;
  /** グローバルオーディオコントロールが ON なら自動再生を試みる */
  withSound: boolean;
}

/**
 * 音声ピン: メッセンジャーと同じコンパクトプレイヤー。
 * ピン位置へ事前にシークし、グローバルオーディオが ON なら自動再生を試みる
 * （ブラウザにブロックされた場合はプレイヤーの再生ボタンに委ねる）。
 */
const PinAudio = ({ item, startSeconds, withSound }: PinAudioProps) => {
  const playerRef = useRef<MediaPreviewHandle>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (startSeconds > 0) playerRef.current?.seekTo(startSeconds);
      if (withSound) {
        playerRef.current?.play().catch(() => undefined);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [startSeconds, withSound]);

  return (
    <MediaPreview
      ref={playerRef}
      src={item.optimized_url || item.original_url}
      mediaType="audio"
      compactAudio
      waveformUrl={item.waveform_url || undefined}
      fileName={item.variation_name || item.file_name}
      style={{ width: "100%" }}
    />
  );
};

// ── カード本体 ────────────────────────────────────────────────────────

interface HoverCardContentProps {
  shotId: string;
  commentId: string;
}

/** 本文プレビュー用: Markdown リンクをラベルだけに潰す（[📎 名前](URL) など） */
const stripMarkdownLinks = (text: string): string =>
  text.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1");

const HoverCardContent = ({ shotId, commentId }: HoverCardContentProps) => {
  // グローバルオーディオコントロール（ヘッダーのトグル）に従って音の有無を決める
  const withSound = useSelector(selectIsAudioEnabled) as boolean;
  const detail = useGetShotCommentDetailQuery({
    shot_id: shotId,
    comment_id: commentId,
  });
  const comment = useMemo(
    () => extractDetailEntity<ShotComment>(detail.data),
    [detail.data],
  );

  const mediaItemsQuery = useGetMediaItemsQuery(
    { shot_id: shotId },
    { skip: !comment?.media_item_id },
  );
  const item = useMemo(() => {
    const mediaItemId = comment?.media_item_id;
    if (!mediaItemId) return null;
    return (
      mediaItemsQuery.data?.data?.media_items?.find(
        (m) => m.id === mediaItemId,
      ) ?? null
    );
  }, [mediaItemsQuery.data, comment?.media_item_id]);

  if (!comment) {
    const failed = detail.isError || (detail.isSuccess && !comment);
    return (
      <Card>
        <StateText>
          {failed ? "コメントを読み込めませんでした" : "読み込み中…"}
        </StateText>
      </Card>
    );
  }

  // ピン位置の解決はタイムラインピンと同じ順（本文タグ → timecode_start）
  const timecode = hasTimecode(comment.comment)
    ? extractFirstTimecode(comment.comment)
    : comment.timecode_start || "";
  const startSeconds = timecode
    ? (parseTimecode(timecode, item?.frame_rate ?? DEFAULT_FPS) ?? 0)
    : 0;
  const badgeLabel = formatTimecode(startSeconds);
  const poster = comment.pin_thumbnail_url || item?.thumbnail || null;

  const renderPin = () => {
    if (!comment.media_item_id) return null;
    if (item?.file_type === "video") {
      return (
        <MediaArea>
          <PinVideo
            item={item}
            poster={poster}
            startSeconds={startSeconds}
            withSound={withSound}
          />
          <TimecodeBadge>
            <span className="material-symbols-rounded">play_circle</span>
            {badgeLabel}
          </TimecodeBadge>
        </MediaArea>
      );
    }
    if (item?.file_type === "audio") {
      return (
        <PinAudio
          item={item}
          startSeconds={startSeconds}
          withSound={withSound}
        />
      );
    }
    if (item?.file_type === "image") {
      return (
        <MediaArea>
          <PinImage
            src={item.thumbnail || item.optimized_url || item.original_url}
            alt=""
          />
        </MediaArea>
      );
    }
    // メディア一覧の取得が終わる前でも、ピン時刻のフレーム画像があれば先に見せる
    if (poster && !mediaItemsQuery.isError) {
      return (
        <MediaArea>
          <PinImage src={poster} alt="" />
          <TimecodeBadge>
            <span className="material-symbols-rounded">play_circle</span>
            {badgeLabel}
          </TimecodeBadge>
        </MediaArea>
      );
    }
    // 取得失敗・メディア削除済みなどは本文だけ見せる
    return null;
  };

  return (
    <Card>
      <Header>
        <UserAvatar
          userId={comment.author?.id}
          avatar={comment.author?.avatar}
          displayName={comment.author?.display_name}
          size={20}
        />
        <AuthorName>{comment.author?.display_name}</AuthorName>
        <HeaderDate>
          <DateDisplay
            date={comment.created_at}
            type={DATE_DISPLAY_TYPE.DATE_TIME}
          />
        </HeaderDate>
      </Header>
      {comment.comment && <Body>{stripMarkdownLinks(comment.comment)}</Body>}
      {comment.files?.length > 0 && (
        <StateText>添付ファイル {comment.files.length}件</StateText>
      )}
      {renderPin()}
    </Card>
  );
};

// ── 公開 API ─────────────────────────────────────────────────────────

/** `/{projectId}/{shotId}?comment={id}` からショット ID とコメント ID を取り出す */
const parseShotCommentTo = (
  to: string,
): { shotId: string; commentId: string } | null => {
  let url: URL;
  try {
    url = new URL(to, window.location.origin);
  } catch {
    return null;
  }
  const segments = url.pathname.split("/").filter(Boolean);
  const commentId = url.searchParams.get("comment");
  if (segments.length !== 2 || !commentId) return null;
  return { shotId: segments[1], commentId };
};

/** ホバーできるポインタがある環境か（タッチのみの端末ではカードを出さない） */
const canHover = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

interface ShotCommentHoverProps {
  shotId: string;
  commentId: string;
  /** ホバー対象（ref を受け取れる単一要素） */
  children: ReactElement;
}

/**
 * 任意の要素にショットコメントのホバーカードを付ける汎用ラッパー
 * （ディープリンクチップのほか、タスクボードの CB バッジ等から使う）。
 */
export const ShotCommentHover = ({
  shotId,
  commentId,
  children,
}: ShotCommentHoverProps) => {
  // 開いている間だけ中身をマウントする（閉じたら動画・音声を確実に止める）
  const [contentMounted, setContentMounted] = useState(false);

  if (!canHover()) return children;

  return (
    <Tippy
      content={
        contentMounted ? (
          <HoverCardContent shotId={shotId} commentId={commentId} />
        ) : (
          <span />
        )
      }
      theme="shot-comment-hover"
      arrow={false}
      interactive
      interactiveBorder={8}
      appendTo={appendToTippyContainer}
      delay={[400, 100]}
      duration={[150, 150]}
      placement="top-start"
      offset={[0, 6]}
      maxWidth="none"
      touch={false}
      onShow={() => setContentMounted(true)}
      onHidden={() => setContentMounted(false)}
    >
      {children}
    </Tippy>
  );
};

interface ShotCommentLinkHoverProps {
  /** parseInternalLink の `to`（/{projectId}/{shotId}?comment=...） */
  to: string;
  /** ホバー対象のチップ（ref を受け取れる単一要素） */
  children: ReactElement;
}

export const ShotCommentLinkHover = ({
  to,
  children,
}: ShotCommentLinkHoverProps) => {
  const ids = useMemo(() => parseShotCommentTo(to), [to]);

  if (!ids) return children;

  return (
    <ShotCommentHover shotId={ids.shotId} commentId={ids.commentId}>
      {children}
    </ShotCommentHover>
  );
};
